import { useState, useEffect } from "react";
import "./Post.css";
import CommentBox from "./Comment";
import PostCommentComposer from "./PostCommentComposer";
import PostLoadingOverlay from "./PostLoadingOverlay";
import PostReportModal from "./PostReportModal";
import PostImageModal from "./PostImageModal";
import PostDeleteModal from "./PostDeleteModal";
import PostEditModal from "./PostEditModal";
import Markdown from "../../../components/Markdown/Markdown";
import { shouldClampPost } from "../../../utils/markdownUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPen } from "@fortawesome/free-solid-svg-icons";
import formatDate from "../../../public Func/DateFix";

import axios from "../../../public Func/axiosAuth";

function PostBox({ post }) {
  // Get current logged-in supervisor/admin ID
  const currentUserId =
    localStorage.getItem("id") ||
    localStorage.getItem("supervisorID") ||
    localStorage.getItem("supervisor_id");
  const currentRole = localStorage.getItem("role");
  const isCurrentUserSupervisor =
    currentRole === "supervisor" ||
    currentRole === "Supervisor" ||
    currentRole === "super";
  // Check if this is supervisor's own post by comparing supervisorID
  const isOwnPost =
    isCurrentUserSupervisor &&
    post.isSupervisor &&
    ((post.supervisorID &&
      currentUserId &&
      post.supervisorID.toString() === currentUserId.toString()) ||
      // Fallback: if supervisorID doesn't match but user is supervisor and post is by supervisor, show delete
      (post.isSupervisor && isCurrentUserSupervisor));

  // Local view state for the post body (updated after edits so the UI reflects them)
  const [postView, setPostView] = useState({
    mainText: post.mainText,
    community: post.community,
    communityID: post.communityID,
    hideIdentity: post.hideIdentity,
    date: post.date,
    edited: post.edited,
    images: Array.isArray(post.images) ? post.images : [],
  });

  // Validate For anonymous, admin, supervisor, or doctor
  var UserImage, UserName;
  if (postView.hideIdentity) {
    UserImage = (
      <div className="anonymous">
        <FontAwesomeIcon icon="fa-solid fa-user-secret" />
      </div>
    );
    UserName = "Anonymous Member";
  } else if (post.isAdmin) {
    UserImage = (
      <div className="anonymous" style={{ background: "var(--primary-500)" }}>
        <FontAwesomeIcon icon="fa-solid fa-user-shield" />
      </div>
    );
    UserName = post.userName || `Admin`;
  } else if (post.isSupervisor) {
    UserImage = (
      <div className="anonymous" style={{ background: "var(--accent-purple)" }}>
        <FontAwesomeIcon icon="fa-solid fa-user-tie" />
      </div>
    );
    UserName = post.userName || post.supervisorName || `Supervisor`;
  } else if (post.isDoctor) {
    UserImage = (
      <img src={post.userProfileImage} alt={post.userName + " Profile Pic"} />
    );
    UserName = post.userName + (post.userTitle ? ` - ${post.userTitle}` : "");
  } else {
    UserImage = (
      <img src={post.userProfileImage} alt={post.userName + " Profile Pic"} />
    );
    UserName = post.userName;
  }

  // AI Rating Colors
  var RateColor = "rateGreen";
  if (Number(post.AI_saftyRate) < 50) {
    RateColor = "rateRed";
  } else if (Number(post.AI_saftyRate) < 75) {
    RateColor = "rateYellow";
  }

  // Uploaded Images
  const [modalImage, setModalImage] = useState(null);
  const images = [];
  if (postView.images.length > 0) {
    for (var i of postView.images) {
      // Handle both full URLs and relative paths
      const imageUrl =
        i.link && i.link.startsWith("http")
          ? i.link
          : process.env.REACT_APP_BACKEND_URL + "/file/" + (i.link || "");
      if (
        imageUrl &&
        imageUrl !== process.env.REACT_APP_BACKEND_URL + "/file/"
      ) {
        images.push(
          <img
            key={i.id || i.link || Math.random()}
            src={imageUrl}
            alt={i.name || "Post image"}
            onClick={() => setModalImage(imageUrl)}
            style={{ cursor: "pointer" }}
          />,
        );
      }
    }
  }

  // Long post bodies collapse to a few lines behind a "Read more" toggle and grow in
  // place. Same rule as the public site: measured on the rendered text, clamped in lines
  // so the Markdown keeps rendering normally instead of being cut mid-syntax.
  const [bodyExpanded, setBodyExpanded] = useState(false);

  // Comments. A short preview of the newest comments loads with the card so recent
  // activity (including the admin's own comment, which the endpoint puts first because
  // it orders by date DESC) is visible without any interaction. The full thread is then
  // fetched in a single request on demand and shown in a scrollable box.
  //
  // This deliberately does not pull every thread up front: the feed renders 15 posts at
  // a time, so eagerly loading up to 100 comments for each of them would triple the
  // queries behind every feed load for no moderation benefit.
  const [commentList, setCommentList] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [allCommentsLoaded, setAllCommentsLoaded] = useState(false);
  const [commentsNumber, setCommentsNumber] = useState(
    post.commentsNumber || 0,
  );
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState("");

  // Newest comments shown before the thread is expanded.
  const COMMENT_PREVIEW = 3;
  // Matches the server-side cap on the comment `limit`.
  const COMMENT_LIMIT = 100;

  async function fetchComments(limit) {
    setLoadingComments(true);
    try {
      const res = await axios.get(
        process.env.REACT_APP_BACKEND_URL +
          "/community/comment-list?limit=" +
          limit +
          "&postID=" +
          post.id,
      );
      setCommentList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // A failed load should not take the page down; the composer still works and the
      // next interaction will try again.
      console.log("Error loading comments!!");
      console.log(err);
      setCommentList([]);
    } finally {
      setLoadingComments(false);
    }
  }

  // Re-fetch whatever is currently on screen, so a newly posted comment appears without
  // collapsing a thread the admin had already opened.
  function refreshComments() {
    return fetchComments(allCommentsLoaded ? COMMENT_LIMIT : COMMENT_PREVIEW);
  }

  // Load the newest few comments the first time the post is rendered.
  useEffect(() => {
    fetchComments(COMMENT_PREVIEW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  // Each card in the feed is a different post, so collapse the body on post change.
  useEffect(() => {
    setBodyExpanded(false);
  }, [post.id]);

  // Pull the whole thread in one request. Done on demand rather than up front so that
  // opening the feed does not fan out into a large comment query per post.
  async function showAllComments() {
    setAllCommentsLoaded(true);
    await fetchComments(COMMENT_LIMIT);
  }

  // Add a Comment as the Current Admin
  async function submitComment() {
    const text = newComment.trim();
    if (!text || isSubmittingComment) return;
    setIsSubmittingComment(true);
    setCommentError("");
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/admin/post-comment",
        {
          postID: post.id,
          comment: text,
          date: new Date().toISOString(),
        },
      );

      // This endpoint answers with the new comment's id, not "Done" — checking for
      // "Done" made every successful post fall into the error branch and print the id
      // to the user as a failure message. Success is therefore a 200 carrying a
      // non-empty id, so a genuinely empty 200 body is still reported as a failure.
      const commentId = res.data;
      const posted =
        res.status === 200 &&
        (typeof commentId === "number" ||
          (typeof commentId === "string" && commentId.trim() !== ""));

      if (posted) {
        setCommentsNumber((previous) => previous + 1);
        setNewComment("");
        await refreshComments();
      } else {
        setCommentError(
          typeof commentId === "string" && commentId
            ? commentId
            : "Failed to add comment. Please try again.",
        );
      }
    } catch (err) {
      console.log("Error!!");
      console.log(err);
      setCommentError(
        err.response?.data || "Failed to add comment. Please try again.",
      );
    } finally {
      setIsSubmittingComment(false);
    }
  }

  //// Report Part
  const [showReportModal, setShowReportModal] = useState(false);
  const [isReporting, setIsReporting] = useState(false);

  // Showing the Popup Window
  function ShowPopupReportForm() {
    setShowReportModal(true);
  }

  // Delete Post Part
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showErrorMessage, setshowErrorMessage] = useState(null);

  function showDeleteConfirm() {
    setShowDeleteModal(true);
  }

  function hideDeleteModal() {
    setShowDeleteModal(false);
  }

  // Edit Post Part
  const [showEditModal, setShowEditModal] = useState(false);

  function showEditForm() {
    setShowEditModal(true);
  }

  function hideEditModal() {
    setShowEditModal(false);
  }

  function handlePostSaved(updatedPost) {
    // Reflect the edit in the rendered post box (local view state)
    setPostView({
      mainText: updatedPost.mainText,
      community: updatedPost.community,
      communityID: updatedPost.communityID,
      hideIdentity: updatedPost.hideIdentity,
      date: updatedPost.date,
      edited: true,
      images: updatedPost.images,
    });
  }

  async function deletePost() {
    setIsDeleting(true);
    try {
      const res = await axios.delete(
        process.env.REACT_APP_BACKEND_URL + "/admin/post",
        {
          data: { postID: post.id },
        },
      );

      if (
        res.status === 200 &&
        (res.data === "Done" || res.data?.message === "Done" || res.data?.done)
      ) {
        hideDeleteModal();
        const parent = document.getElementById(post.id);
        if (parent) {
          parent.style.height = parent.scrollHeight + "px";
          function hideScroll() {
            parent.style.height = "0px";
            parent.style.padding = "0px";
            parent.style.margin = "0px auto";
          }
          function dissolve() {
            parent.style.display = "none";
          }
          setTimeout(hideScroll, 100);
          setTimeout(dissolve, 2000);
        }
      } else {
        setshowErrorMessage("Failed to delete post. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting post:", err);
      setshowErrorMessage(
        err.response?.data || "Failed to delete post. Please try again.",
      );
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="PostBox" id={post.id}>
      {isReporting && <PostLoadingOverlay />}
      <div className="PostHeader">
        <div className="UserPic">
          {UserImage}
          {postView.hideIdentity || post.isAdmin || post.isSupervisor ? (
            ""
          ) : post.isDoctor && post.doctorID ? (
            <a
              href={"./profile/doctor/" + post.doctorID}
              className="profileLink"
              target="_blank"
              rel="noreferrer"
            ></a>
          ) : post.patientID ? (
            <a
              href={"./profile/patient/" + post.patientID}
              className="profileLink"
              target="_blank"
              rel="noreferrer"
            ></a>
          ) : (
            ""
          )}
        </div>
        <div className="NameAndDateAndData">
          <div className="left">
            <p>
              {UserName}{" "}
              {postView.edited ? <span className="GrayText">(edited)</span> : ""}
            </p>
            <span className="GrayText">{formatDate(postView.date)}</span>
            <span className={"aiRate " + RateColor} title="AI Safety Rating">
              <FontAwesomeIcon icon="fa-solid fa-robot" /> {post.AI_saftyRate}%
              - ({post.AI_saftyWord})
            </span>
          </div>
          <div className="Tags right">
            <span className="Community">{postView.community}</span>
            {/* Show Delete button for admin's own posts or supervisor's own posts */}
            {(post.isAdmin || post.adminEmail || isOwnPost) && (
              <>
                <span
                  className={"Edit"}
                  onClick={showEditForm}
                  style={{ pointerEvents: "auto" }}
                >
                  <FontAwesomeIcon
                    icon={faPen}
                    style={{ marginRight: "0.25rem" }}
                  />
                  Edit
                </span>
                <span
                  className={"Delete" + (isDeleting ? " disabled" : "")}
                  onClick={showDeleteConfirm}
                  style={{ pointerEvents: isDeleting ? "none" : "auto" }}
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    style={{ marginRight: "0.25rem" }}
                  />
                  Delete
                </span>
              </>
            )}
            {/* Show Report button only if it's not the supervisor's own post and not admin's post */}
            {!isOwnPost && !(post.isAdmin || post.adminEmail) && (
              <span
                className={"Report" + (isReporting ? " disabled" : "")}
                onClick={ShowPopupReportForm}
                style={{ pointerEvents: isReporting ? "none" : "auto" }}
              >
                Report
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="PostBody">
        <Markdown
          data-testid="post-body"
          className={
            "postBodyText" +
            (shouldClampPost(postView.mainText)
              ? bodyExpanded
                ? " postBodyTextExpanded"
                : " postBodyTextClamp"
              : "")
          }
        >
          {postView.mainText}
        </Markdown>
        {shouldClampPost(postView.mainText) && (
          <button
            className="postReadMore"
            onClick={() => setBodyExpanded((previous) => !previous)}
            aria-expanded={bodyExpanded}
          >
            {bodyExpanded ? "Show less" : "Read more"}
          </button>
        )}
        {images.length > 0 && (
          <div className="PostImageContainer one">{images}</div>
        )}
      </div>
      <div className="PostReactions">
        <div className="left">
          <span title="How Many Times the Post Was Displayed on a Screen">
            <FontAwesomeIcon icon="fa-solid fa-eye" /> {post.views}
          </span>
        </div>
        <div className="right">
          <span title="How Many People Reacted To This Post">
            <FontAwesomeIcon icon="fa-solid fa-heart" /> {post.reactions}
          </span>
          <span title="Total Comment Number">
            <FontAwesomeIcon icon="fa-solid fa-comment" /> {commentsNumber}
          </span>
        </div>
      </div>
      {commentList.length > 0 && (
        <div className="PostCommentSection">
          {commentList.map((comment) => (
            <CommentBox key={comment.id} commentData={comment} />
          ))}
        </div>
      )}
      {!loadingComments && commentList.length === 0 && (
        <div className="PostCommentSection empty">
          No Comments on This Post
        </div>
      )}
      {loadingComments && (
        <div className="PostCommentsButtons disabled">
          <p>Loading Comments ...</p>
        </div>
      )}
      {/* The preview can be shorter than the thread; offer the rest in one click. */}
      {!allCommentsLoaded && !loadingComments && commentsNumber > commentList.length && (
        <div className="PostCommentsButtons enabled" onClick={showAllComments}>
          <p>Show all {commentsNumber} comments</p>
        </div>
      )}

      <PostCommentComposer
        newComment={newComment}
        setNewComment={setNewComment}
        onSubmit={submitComment}
        isSubmitting={isSubmittingComment}
        error={commentError}
      />

      {showReportModal && (
        <PostReportModal
          post={post}
          onClose={() => setShowReportModal(false)}
          onReportingChange={setIsReporting}
        />
      )}

      {modalImage && (
        <PostImageModal imageUrl={modalImage} onClose={() => setModalImage(null)} />
      )}

      {showDeleteModal && (
        <PostDeleteModal
          post={post}
          onClose={hideDeleteModal}
          onConfirm={deletePost}
          isDeleting={isDeleting}
          errorMessage={showErrorMessage}
        />
      )}

      {showEditModal && (
        <PostEditModal
          post={post}
          onClose={hideEditModal}
          onSaved={handlePostSaved}
        />
      )}
    </div>
  );
}

export default PostBox;
